<template>
  <div>
    <div v-if="!condicoes.length" class="condicoes-vazio">
      Nenhuma condição cadastrada. A regra vale sobre o pedido inteiro.
    </div>

    <div
      v-for="(condicao, index) in condicoes"
      :key="`condicao-${index}`"
      class="regra"
      :class="{ 'regra-and': validarTodasRegras }"
    >
      <div class="regra-section-label">Regra de validação — valida sobre os segmentos:</div>

      <div class="regra-container">
        <div v-for="dimensao in colunas" :key="`v-${dimensao.coluna}`" class="regra-dimensao">
          <label>{{ dimensao.label }}</label>
          <select
            v-if="dimensao.dimensoes.length"
            v-model="condicao[`dimensao${dimensao.coluna}`]"
            @change="clonarAplicacao(condicao)"
          >
            <option value="">—</option>
            <option
              v-for="itemDimensao in dimensao.dimensoes"
              :key="itemDimensao.id"
              :value="itemDimensao.id"
            >
              {{ itemDimensao.descricao || itemDimensao.desc }}
            </option>
          </select>
          <input
            v-else
            type="text"
            v-model="condicao[`dimensao${dimensao.coluna}`]"
            @change="clonarAplicacao(condicao)"
            placeholder="—"
          />
        </div>
      </div>

      <hr class="regra-divider" />

      <div class="regra-section-label">Regra de aplicação — aplica sobre os segmentos:</div>
      <div class="regra-aplicacao-check">
        <label>
          <input
            type="checkbox"
            v-model="condicao.aplicaregra"
            @change="clonarAplicacao(condicao)"
          />
          Aplicar o desconto nos mesmos itens da regra de validação
        </label>
      </div>

      <div class="regra-container">
        <div v-for="dimensao in colunas" :key="`a-${dimensao.coluna}`" class="regra-dimensao">
          <label>{{ dimensao.label }}</label>
          <select
            v-if="dimensao.dimensoes.length"
            v-model="condicao[`dimensaoaplicacao${dimensao.coluna}`]"
            :disabled="!!condicao.aplicaregra"
          >
            <option value="">—</option>
            <option
              v-for="itemDimensao in dimensao.dimensoes"
              :key="itemDimensao.id"
              :value="itemDimensao.id"
            >
              {{ itemDimensao.descricao || itemDimensao.desc }}
            </option>
          </select>
          <input
            v-else
            type="text"
            v-model="condicao[`dimensaoaplicacao${dimensao.coluna}`]"
            :disabled="!!condicao.aplicaregra"
            placeholder="—"
          />
        </div>
      </div>

      <div v-if="validarPorRegra" class="regra-faixa">
        <div class="regra-section-label">Faixa de valor desta condição</div>
        <div class="regra-faixa-inputs">
          <div>
            <label>Valor mínimo</label>
            <InputMoney v-model="condicao.valorminimo" />
          </div>
          <div>
            <label>Valor máximo</label>
            <InputMoney v-model="condicao.valormaximo" />
            <small>R$ 0,00 = sem teto.</small>
          </div>
        </div>
      </div>
      <div v-else class="regra-faixa-herdada">
        A faixa de valor vem da regra. Para dar uma faixa própria a cada condição, ligue
        <strong>Faixa por condição</strong> na aba Regras.
      </div>

      <div class="regra-footer">
        <div class="regra-footer__desc-input">
          Percentual de
          <input
            type="number"
            step="0.01"
            v-model.number="condicao.percdesconto"
            @keydown="bloqueiaNotacaoCientifica($event)"
          />
          <span :class="classePercentual(condicao.percdesconto)">
            {{ rotuloPercentual(condicao.percdesconto) }}
          </span>
          nos itens da regra de aplicação
        </div>
        <!--
          Modo "liberação de limite" da proposta: a regra não aplica nada sozinha,
          só autoriza o vendedor a chegar até aqui. Convive com a aplicação
          automática, e o teto acompanha a direção do percentual — numa condição de
          acréscimo ele também é negativo, porque um limite positivo não diria nada
          sobre ela.
        -->
        <div class="regra-footer__teto">
          Percentual máximo de
          <input
            type="number"
            step="0.01"
            v-model.number="condicao.percmaximodesc"
            @keydown="bloqueiaNotacaoCientifica($event)"
            placeholder="—"
          />
          <span :class="classePercentual(condicao.percmaximodesc)">
            {{ rotuloPercentual(condicao.percmaximodesc) }}
          </span>
          nos itens da regra de aplicação
        </div>
        <button type="button" class="regra-footer__delete" @click="excluirCondicao(index)">
          <CIcon name="cil-trash" size="sm" /> Excluir
        </button>
      </div>
    </div>

    <button type="button" class="btn-adicionar" @click="adicionarCondicao">
      + Adicionar condição
    </button>
  </div>
</template>

<script>
const TOTAL_DIMENSOES = 10;

const condicaoVazia = () => {
  const condicao = {
    valorminimo: 0,
    valormaximo: null,
    percdesconto: 0,
    percmaximodesc: null,
    aplicaregra: true,
  };

  for (let n = 1; n <= TOTAL_DIMENSOES; n++) {
    condicao[`dimensao${n}`] = "";
    condicao[`dimensaoaplicacao${n}`] = "";
  }

  return condicao;
};

import InputMoney from "@/components/InputMoney";

export default {
  name: "DescontoCondicoes",
  components: { InputMoney },
  props: {
    condicoes: { type: Array, required: true },
    dimensoes: { type: Array, default: () => [] },
    validarPorRegra: { type: Boolean, default: false },
    validarTodasRegras: { type: Boolean, default: false },
  },
  computed: {
    /**
     * A lista de dimensoes so traz as colunas que tem opcoes cadastradas: numa
     * base sem esse cadastro ela volta vazia e a condicao ficaria sem campo
     * nenhum para preencher. Sem opcoes, a coluna e digitada como codigo —
     * mesmo caminho da aba Restricoes · Produto.
     */
    colunas() {
      if (this.dimensoes.length) return this.dimensoes;

      return Array.from({ length: TOTAL_DIMENSOES }, (item, indice) => ({
        coluna: indice + 1,
        label: `Cód. Dim. ${indice + 1}`,
        dimensoes: [],
      }));
    },
  },
  methods: {
    adicionarCondicao() {
      this.condicoes.push(condicaoVazia());
    },
    excluirCondicao(index) {
      this.condicoes.splice(index, 1);
    },
    clonarAplicacao(condicao) {
      if (!condicao.aplicaregra) return;

      for (let n = 1; n <= TOTAL_DIMENSOES; n++) {
        condicao[`dimensaoaplicacao${n}`] = condicao[`dimensao${n}`];
      }
    },
    /**
     * Positivo desconta, negativo acresce. O rotulo ao lado do campo diz qual
     * dos dois a linha esta fazendo, para o sinal nao passar despercebido.
     */
    /**
     * Positivo desconta, negativo acresce. Recebe o valor em vez da condição
     * porque serve tanto ao percentual aplicado quanto ao teto do vendedor — os
     * dois usam a mesma coluna de sinal e precisam do mesmo rótulo.
     */
    rotuloPercentual(percentual) {
      const valor = Number(percentual) || 0;

      if (valor > 0) return '% de desconto';
      if (valor < 0) return '% de acréscimo';

      return '%';
    },
    classePercentual(percentual) {
      const valor = Number(percentual) || 0;

      if (valor > 0) return 'percentual--desconto';
      if (valor < 0) return 'percentual--acrescimo';

      return '';
    },
    /** Faixa de valor do pedido nao e negativa. */
    /**
     * O percentual aceita os dois sinais — a mesma coluna guarda desconto e
     * acrescimo. Só a notacao cientifica fica de fora, que o `type=number`
     * aceitaria e o servidor nao entende.
     */
    bloqueiaNotacaoCientifica(event) {
      if (["e", "E"].includes(event.key)) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
.condicoes-vazio {
  padding: 18px;
  text-align: center;
  color: #888;
  font-size: 13px;
  border: 1px dashed #dddce9;
  border-radius: 10px;
  margin-bottom: 16px;
}

.regra {
  position: relative;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
}

/* O vão precisa caber o selo E/OU com folga dos dois lados: o selo tem ~18px
   (11px de fonte + 2px de padding em cima e embaixo), e num vão de 24px sobravam
   uns 4px, colando o selo no cartão de baixo. Com 40px e o selo em -29px, ficam
   ~11px livres acima e abaixo dele. */
.regra + .regra {
  margin-top: 40px;
}

.regra + .regra::before {
  position: absolute;
  text-align: center;
  left: 50%;
  transform: translateX(-50%);
  content: "OU";
  top: -29px;
  font-size: 11px;
  font-weight: 700;
  color: #888;
  background: #f4f5f7;
  padding: 2px 12px;
  border-radius: 10px;
  letter-spacing: 0.5px;
}

.regra-and + .regra-and::before {
  content: "E";
}

.regra-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #888;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.regra-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.regra-dimensao label {
  font-size: 11px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
  display: block;
}

.regra-dimensao select,
.regra-dimensao input[type="text"] {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 6px 8px;
  width: 100%;
  background-color: #fff;
}

.regra-dimensao select:focus,
.regra-dimensao input[type="text"]:focus {
  border-color: #3a3e81;
  outline: none;
  box-shadow: 0 0 0 2px rgba(58, 62, 129, 0.1);
}

.regra-divider {
  border: none;
  border-top: 1px dashed #e4e7eb;
  margin: 4px 0 14px;
}

.regra-aplicacao-check {
  margin-bottom: 12px;
  font-size: 13px;
}

.regra-aplicacao-check input {
  margin-right: 6px;
}

.regra-faixa {
  border-top: 1px dashed #e4e7eb;
  padding-top: 12px;
  margin-bottom: 12px;
}

.regra-faixa-inputs {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}

.regra-faixa-inputs label {
  font-size: 11px;
  font-weight: 600;
  color: #555;
  margin-bottom: 4px;
  display: block;
}

/* O InputMoney renderiza com `form-control`, que impõe height fixo e desalinha
   estes campos do resto da sub-tela. O seletor com o atributo de escopo tem
   precedência sobre a classe global. */
.regra-faixa-inputs input {
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 6px 8px;
  width: 100%;
  height: auto;
  line-height: normal;
}

.regra-faixa-inputs small {
  font-size: 11px;
  color: #999;
}

.regra-faixa-herdada {
  font-size: 12px;
  color: #888;
  border-top: 1px dashed #e4e7eb;
  padding-top: 12px;
  margin-bottom: 12px;
}

.regra-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  border-top: 1px solid #eee;
  padding-top: 12px;
  font-size: 13px;
}

.regra-footer__desc-input input {
  width: 90px;
  margin: 0 6px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 4px 8px;
}

.percentual--desconto {
  color: #12a594;
  font-weight: 600;
}

.percentual--acrescimo {
  color: #e5484d;
  font-weight: 600;
}

.regra-footer__teto {
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.regra-footer__teto input {
  width: 80px;
  font-size: 13px;
  border-radius: 6px;
  border: 1px solid #dddce9;
  padding: 4px 8px;
}

/* Mesmo botão do cadastro de campanha comercial (RegraSegmento.vue): borda
   neutra, e o vermelho só no hover. */
.regra-footer__delete {
  background: none;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  color: #999;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.regra-footer__delete:hover {
  border-color: #e74c3c;
  color: #e74c3c;
  background: #fef2f2;
}

.btn-adicionar {
  background: #fff;
  border: 1px dashed #3a3e81;
  color: #3a3e81;
  font-size: 13px;
  font-weight: 600;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  width: 100%;
}

.btn-adicionar:hover {
  background: #f4f5ff;
}
</style>
